import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";

interface CopyRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceFieldId: string;
  sourceFieldName: string;
  onCopyComplete: () => void;
}

export const CopyRulesDialog = ({
  open,
  onOpenChange,
  sourceFieldId,
  sourceFieldName,
  onCopyComplete,
}: CopyRulesDialogProps) => {
  const [targetFieldId, setTargetFieldId] = useState<string>("");
  const [onlyEnabled, setOnlyEnabled] = useState<boolean>(true);
  const [replaceExisting, setReplaceExisting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Fetch all fields except the current one
  const { data: fields, isLoading } = useQuery({
    queryKey: ["fields-for-copy", sourceFieldId],
    queryFn: async () => {
      const { data, error } = await supabase.from("fields").select("id, name").neq("id", sourceFieldId).order("name");

      if (error) throw error;
      return data;
    },
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setTargetFieldId("");
      setOnlyEnabled(true);
      setReplaceExisting(false);
      setSearchTerm("");
    }
  }, [open]);

  // Filter fields based on search term
  const filteredFields = fields?.filter((field) => field.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleCopyRules = async () => {
    if (!targetFieldId) {
      toast.error("Please select a destination field");
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Get rules from source field
      const { data: sourceRules, error: fetchError } = await supabase
        .from("field_rules")
        .select("*")
        .eq("field_id", sourceFieldId)
        .order("created_at");

      if (fetchError) throw fetchError;

      if (!sourceRules || sourceRules.length === 0) {
        toast.warning("No rules to copy from the source field");
        setIsSubmitting(false);
        return;
      }

      // Filter out disabled rules if onlyEnabled is true
      let rulesToCopy = sourceRules;
      if (onlyEnabled) {
        rulesToCopy = rulesToCopy.filter((rule) => rule.enabled);
      }

      if (rulesToCopy.length === 0) {
        toast.warning("No enabled rules found to copy");
        setIsSubmitting(false);
        return;
      }

      // 2. If replacing existing, delete target field rules
      if (replaceExisting) {
        const { error: deleteError } = await supabase.from("field_rules").delete().eq("field_id", targetFieldId);

        if (deleteError) throw deleteError;
      }

      // 3. Copy rules to target field
      const newRules = rulesToCopy.map((rule) => ({
        field_id: targetFieldId,
        rule_text: rule.rule_text,
        enabled: rule.enabled,
      }));

      const { error: insertError } = await supabase.from("field_rules").insert(newRules);

      if (insertError) throw insertError;

      toast.success(`Rules successfully copied to the destination field`);
      onCopyComplete();
      onOpenChange(false);
    } catch (error) {
      console.error("Error copying rules:", error);
      toast.error("Failed to copy rules");
    } finally {
      setIsSubmitting(false);
    }
  };

  const _targetField = fields?.find((f) => f.id === targetFieldId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Copy Rules</DialogTitle>
          <DialogDescription>
            Copy rules from <strong>{sourceFieldName}</strong> to another field
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="target-field">Destination Field</Label>
            <div className="relative">
              <div className="mb-2 flex w-full items-center space-x-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9"
                  disabled={isLoading || isSubmitting}
                />
              </div>
              <Select value={targetFieldId} onValueChange={setTargetFieldId} disabled={isLoading || isSubmitting}>
                <SelectTrigger id="target-field">
                  <SelectValue placeholder="Select a field" />
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="h-[200px]">
                    {isLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading fields...
                      </SelectItem>
                    ) : filteredFields?.length === 0 ? (
                      <SelectItem value="no-results" disabled>
                        No fields match your search
                      </SelectItem>
                    ) : (
                      filteredFields?.map((field) => (
                        <SelectItem key={field.id} value={field.id}>
                          {field.name}
                        </SelectItem>
                      ))
                    )}
                  </ScrollArea>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch id="only-enabled" checked={onlyEnabled} onCheckedChange={setOnlyEnabled} disabled={isSubmitting} />
            <Label htmlFor="only-enabled">Copy only enabled rules</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="replace-existing"
              checked={replaceExisting}
              onCheckedChange={setReplaceExisting}
              disabled={isSubmitting}
            />
            <Label htmlFor="replace-existing">Replace existing rules in destination field</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleCopyRules} disabled={!targetFieldId || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Copying...
              </>
            ) : (
              <>Copy Rules</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
